import React, { useState, useCallback, useMemo } from 'react'
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  useTheme,
  Grid,
  Tooltip,
  Button,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Edit as EditIcon,
  CheckCircleOutlineSharp,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  HelpOutline as HelpOutlineIcon,
  AutoFixHigh as MagicWand,
} from '@mui/icons-material'
import MarkdownPreview from '@uiw/react-markdown-preview'
import { toast } from 'atoms/Toast'
import { useGeneratingLabel } from './MappingEditor'
import { useArtifactGeneration } from '../../hooks/useArtifactGeneration'
import { useTranslationMapDetailsContext } from '../../contexts/TranslationMapDetailsContext'
import DataModelSchemaStructureDrawer from './DataModelSchemaStructureDrawer'
import DataModelSchemaSelector from 'common/components/selectors/DataModelSchemaSelector'
import ContentTypeTag from 'atoms/ContentTypeTag/ContentTypeTag'
import { useSearchContentTypes } from 'pages/TranslationMaps/api'
import ContentTypeSelector from 'common/components/selectors/ContentTypeSelector'
import SpecificationEditorDialog from './SpecificationEditorDialog'

export interface SpecificationSectionsProps {
  disabled?: boolean
  viewMode?: 'simple' | 'advanced'
}

const SpecificationSections = function SpecificationSections({
  disabled = false,
  viewMode = 'simple',
}: SpecificationSectionsProps) {
  const theme = useTheme()
  const [expandedSections, setExpandedSections] = useState<string[]>([])
  const [editModes, setEditModes] = useState<{ [key: string]: boolean }>({
    summary: false,
    data_mapping: false,
  })

  // Dialog state for editing
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogConfig, setDialogConfig] = useState<{
    title: string
    content: string
    contentType: 'md' | 'json' | 'yaml' | 'xml'
    field: 'summary' | 'data_mapping'
  } | null>(null)

  // Use context for specification management and data models
  const {
    currentSpecification,
    translationMap,
    dataModels,
    isSpecificationDirty,
    dialogs,
    updateTranslationMap,
    updateSpecificationField,
    closeInputDataModelStructureDialog,
    closeOutputDataModelStructureDialog,
    updateInputDataModel,
    updateOutputDataModel,
    openInputDataModelStructureDialog,
    openOutputDataModelStructureDialog,
    mappingEditor,
  } = useTranslationMapDetailsContext()

  const { setActiveTab } = mappingEditor

  const { isRunning: isGeneratingArtifact, generate } = useArtifactGeneration(translationMap?.id, {
    onCompleted: useCallback(() => setActiveTab(1), [setActiveTab]),
  })
  const generatingLabel = useGeneratingLabel(isGeneratingArtifact)

  const handleGenerateArtifact = useCallback(() => {
    if (!translationMap?.id) {
      toast.error('Translation map ID is required to generate artifact')
      return
    }

    generate()
  }, [translationMap?.id, generate])

  // Local state for FileViewer content to prevent re-rendering
  const [localSummaryContent, setLocalSummaryContent] = useState(
    currentSpecification?.summary || ''
  )
  const [localDataMappingContent, setLocalDataMappingContent] = useState(
    currentSpecification?.data_mapping || ''
  )

  const { data: contentTypesList } = useSearchContentTypes()

  // Update local content when specification changes, but only if not in edit mode and no pending changes
  React.useEffect(() => {
    if (
      !editModes.summary &&
      !isSpecificationDirty &&
      currentSpecification?.summary !== undefined
    ) {
      setLocalSummaryContent(currentSpecification.summary)
    }
  }, [currentSpecification?.summary, editModes.summary, isSpecificationDirty])

  React.useEffect(() => {
    if (
      !editModes.data_mapping &&
      !isSpecificationDirty &&
      currentSpecification?.data_mapping !== undefined
    ) {
      setLocalDataMappingContent(currentSpecification.data_mapping)
    }
  }, [currentSpecification?.data_mapping, editModes.data_mapping, isSpecificationDirty])

  // Initialize local content when specification becomes available
  React.useEffect(() => {
    if (currentSpecification && !isSpecificationDirty) {
      if (currentSpecification.summary !== undefined && localSummaryContent === '') {
        setLocalSummaryContent(currentSpecification.summary)
      }
      if (currentSpecification.data_mapping !== undefined && localDataMappingContent === '') {
        setLocalDataMappingContent(currentSpecification.data_mapping)
      }
    }
  }, [currentSpecification, isSpecificationDirty, localSummaryContent, localDataMappingContent])

  const handleChange = useCallback(
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSections(prev => {
        if (isExpanded) {
          return prev.includes(panel) ? prev : [...prev, panel]
        } else {
          return prev.filter(section => section !== panel)
        }
      })
    },
    []
  )

  const toggleEditMode = useCallback((section: string) => {
    setEditModes(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }, [])

  // Dialog handlers
  const openEditDialog = useCallback(
    (field: 'summary' | 'data_mapping') => {
      const config = {
        title: field === 'summary' ? 'Edit Summary' : 'Edit Data Mapping',
        content: field === 'summary' ? localSummaryContent : localDataMappingContent,
        contentType: 'md' as const,
        field,
      }
      setDialogConfig(config)
      setDialogOpen(true)
    },
    [localSummaryContent, localDataMappingContent]
  )

  const closeEditDialog = useCallback(() => {
    setDialogOpen(false)
    setDialogConfig(null)
  }, [])

  const handleDialogSave = useCallback(
    (content: string) => {
      if (dialogConfig) {
        if (dialogConfig.field === 'summary') {
          setLocalSummaryContent(content)
          updateSpecificationField('summary', content)
        } else {
          setLocalDataMappingContent(content)
          updateSpecificationField('data_mapping', content)
        }
      }
      closeEditDialog()
    },
    [dialogConfig, updateSpecificationField, closeEditDialog]
  )

  // Helper function to check if data models are configured
  const areDataModelsConfigured = useCallback(() => {
    return !!(dataModels.input && dataModels.output)
  }, [dataModels.input, dataModels.output])

  // Helper function to get status indicator for sections
  const getStatusIndicator = (sectionId: string) => {
    if (sectionId === 'data-models') {
      if (areDataModelsConfigured()) {
        return (
          <CheckCircleIcon
            color="success"
            sx={{ fontSize: 20, ml: 1 }}
            titleAccess="Both data models configured"
          />
        )
      } else {
        return (
          <WarningIcon
            color="warning"
            sx={{ fontSize: 20, ml: 1 }}
            titleAccess="Configure both input and output data models"
          />
        )
      }
    }
    return null
  }

  // Helper function to check if section is disabled
  const isSectionDisabled = (sectionId: string) => {
    if (sectionId === 'data-models') {
      return false
    }
    return !areDataModelsConfigured()
  }

  // Helper function to get tooltip content for each section
  const getSectionTooltip = (sectionId: string) => {
    const tooltips = {
      'data-models':
        'Configure the input and output data models that define the structure of data being transformed. These models are required before configuring other sections.',
      summary:
        'Provide an overview and description of the translation map specification. This section helps document the purpose and scope of the translation.',
      'data-mapping':
        'Define the data mapping configuration and transformation rules. This section contains the actual mapping logic that will guide the data transformation from input to output format.',
    }
    return tooltips[sectionId as keyof typeof tooltips] || ''
  }

  const renderEditButton = (section: string) => (
    <Button
      size="small"
      variant="outlined"
      startIcon={<EditIcon />}
      onClick={() => openEditDialog(section as 'summary' | 'data_mapping')}
      disabled={disabled || isSectionDisabled(section)}
      sx={{ ml: 1 }}
    >
      Edit
    </Button>
  )

  const renderGenerateCodeButton = () => (
    <Button
      size="small"
      variant="contained"
      color="primary"
      startIcon={<MagicWand />}
      onClick={handleGenerateArtifact}
      disabled={disabled || isGeneratingArtifact}
      sx={
        isGeneratingArtifact
          ? { '&.Mui-disabled': { color: 'grey.700' }, textTransform: 'none' }
          : undefined
      }
    >
      {isGeneratingArtifact ? generatingLabel : 'Generate Code'}
    </Button>
  )

  const renderContent = (section: string, value: string) => {
    const sectionDisabled = isSectionDisabled(section)

    return (
      <Box sx={{ p: 2, minHeight: 200 }}>
        {sectionDisabled ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              backgroundColor: 'action.hover',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'divider',
              cursor: 'help',
              position: 'relative',
              '&:hover': {
                backgroundColor: 'action.selected',
              },
            }}
            title="Missing data models configuration"
          >
            <Stack alignItems="center" spacing={1}>
              <WarningIcon color="warning" sx={{ fontSize: 32 }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Configure both input and output data models to enable this section
              </Typography>
            </Stack>
          </Box>
        ) : value ? (
          <div data-color-mode={theme.palette.mode}>
            <MarkdownPreview
              source={value}
              style={{
                padding: 0,
                backgroundColor: 'transparent',
              }}
            />
          </div>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No content available. Click the edit button to add content.
          </Typography>
        )}
      </Box>
    )
  }

  // Common accordion styles
  const accordionStyles = {
    flex: (sectionId: string) => (expandedSections.includes(sectionId) ? 1 : 'none'),
    border: 'none',
    borderRadius: 0,
    '& .MuiAccordionSummary-root': {
      borderRadius: 0,
    },
    '& .MuiAccordionDetails-root': {
      borderRadius: 0,
    },
    '&:first-of-type': {
      borderRadius: 0,
      '& .MuiAccordionSummary-root': {
        borderRadius: 0,
      },
    },
    '&:last-of-type': {
      borderRadius: 0,
      '& .MuiAccordionSummary-root': {
        borderRadius: 0,
      },
    },
    '&.Mui-expanded': {
      borderColor: 'primary.main',
      borderRadius: 0,
      borderTop: '1px solid #E0E0E0',
      '& .MuiAccordionSummary-root': {
        borderRadius: 0,
      },
      '& .MuiAccordionDetails-root': {
        borderRadius: 0,
      },
    },
    '&:not(.Mui-expanded):hover': {
      backgroundColor: 'action.selected',
    },
  }

  const renderSectionHeader = (
    sectionId: string,
    title: string,
    description: string,
    icon: React.ReactElement
  ) => {
    const sectionDisabled = isSectionDisabled(sectionId)

    const headerContent = (
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        disabled={sectionDisabled}
        sx={{
          '& .MuiAccordionSummary-content': {
            justifyContent: 'space-between',
            alignItems: 'center',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            transition: 'transform 0.3s ease-in-out',
            '&.Mui-expanded': {
              transform: 'rotate(180deg)',
            },
          },
          '&:hover': {
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: sectionDisabled ? 'text.disabled' : 'primary.main',
            },
          },
          opacity: sectionDisabled ? 0.6 : 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: sectionDisabled ? 'none' : 'scale(1.1)',
              },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  transition: 'color 0.3s ease-in-out',
                  fontWeight: 600,
                  color: sectionDisabled ? 'text.disabled' : 'inherit',
                }}
              >
                {title}
              </Typography>
              <Tooltip title={getSectionTooltip(sectionId)} arrow placement="top">
                <HelpOutlineIcon
                  sx={{
                    fontSize: 18,
                    color: sectionDisabled ? 'text.disabled' : 'text.secondary',
                    cursor: 'help',
                    transition: 'color 0.3s ease-in-out',
                    '&:hover': {
                      color: sectionDisabled ? 'text.disabled' : 'primary.main',
                    },
                  }}
                />
              </Tooltip>
            </Stack>
            <Typography
              variant="body2"
              color={sectionDisabled ? 'text.disabled' : 'text.secondary'}
              sx={{
                transition: 'color 0.3s ease-in-out',
              }}
            >
              {description}
            </Typography>
          </Box>
        </Stack>
        {getStatusIndicator(sectionId)}
      </AccordionSummary>
    )

    if (sectionDisabled && sectionId !== 'data-models') {
      return (
        <Tooltip title="Missing data models configuration" arrow placement="top">
          <span>{headerContent}</span>
        </Tooltip>
      )
    }

    return headerContent
  }

  // Memoized Data Models Accordion
  const DataModelsAccordion = useMemo(
    () => (
      <Accordion
        expanded={expandedSections.includes('data-models')}
        onChange={handleChange('data-models')}
        sx={{
          ...accordionStyles,
          flex: accordionStyles.flex('data-models'),
        }}
        disableGutters
      >
        {renderSectionHeader(
          'data-models',
          'Data Models',
          'Configure input and output data models for the translation map',
          <CheckCircleOutlineSharp color="primary" />
        )}
        <AccordionDetails
          sx={{ p: 0, height: '100%', '&.MuiAccordionDetails-root': { padding: 0 } }}
        >
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                      >
                        Input Data Model
                      </Typography>
                      {dataModels?.input?.dataModel && (
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      )}
                      <Tooltip
                        title="Select the data model that defines the structure of incoming data"
                        arrow
                        placement="top"
                      >
                        <HelpOutlineIcon
                          sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Box>
                    <DataModelSchemaSelector
                      selected={{
                        label: dataModels?.input?.dataModel?.description,
                        id: dataModels?.input?.dataModel?.id,
                      }}
                      onSelect={dataModel => {
                        updateInputDataModel(dataModel)
                      }}
                    />
                    {dataModels?.input?.dataModel && dataModels.input.contentType && (
                      <Box sx={{ mt: 0.5 }} display="flex" alignItems="center" gap={1}>
                        <ContentTypeTag
                          contentType={translationMap?.input_content_type}
                          size="small"
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: 'primary.dark',
                              textDecoration: 'none',
                            },
                          }}
                          onClick={() => {
                            openInputDataModelStructureDialog()
                          }}
                        >
                          View Structure
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {!dataModels?.input?.dataModel && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                      >
                        Input Content Type
                      </Typography>
                      <ContentTypeSelector
                        selected={{
                          value: dataModels.input.contentType,
                        }}
                        onSelect={contentType => {
                          updateTranslationMap({
                            input_content_type: contentType?.content_type || null,
                          })
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                      >
                        Output Data Model
                      </Typography>
                      {dataModels?.output?.dataModel && (
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      )}
                      <Tooltip
                        title="Select the data model that defines the structure of outgoing data"
                        arrow
                        placement="top"
                      >
                        <HelpOutlineIcon
                          sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Box>
                    <DataModelSchemaSelector
                      selected={{
                        label: dataModels?.output?.dataModel?.description,
                        id: dataModels?.output?.dataModel?.id,
                      }}
                      onSelect={dataModel => {
                        updateOutputDataModel(dataModel)
                      }}
                    />
                    {dataModels?.output?.dataModel && dataModels.output.contentType && (
                      <Box sx={{ mt: 0.5 }} display="flex" alignItems="center" gap={1}>
                        <ContentTypeTag
                          contentType={translationMap?.output_content_type}
                          size="small"
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: 'primary.dark',
                              textDecoration: 'none',
                            },
                          }}
                          onClick={() => {
                            openOutputDataModelStructureDialog()
                          }}
                        >
                          View Structure
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {!dataModels?.output?.dataModel && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                      >
                        Output Content Type
                      </Typography>
                      <ContentTypeSelector
                        selected={{
                          value: dataModels.output.contentType,
                        }}
                        onSelect={contentType => {
                          updateTranslationMap({
                            output_content_type: contentType?.content_type || null,
                          })
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
    ),
    [
      expandedSections,
      handleChange,
      renderSectionHeader,
      translationMap.id,
      dataModels.input,
      dataModels.output,
      viewMode,
    ]
  )

  // Memoized Data Mapping Accordion
  const DataMappingAccordion = useMemo(
    () => (
      <Accordion
        expanded={expandedSections.includes('data-mapping')}
        onChange={handleChange('data-mapping')}
        sx={{
          ...accordionStyles,
          flex: accordionStyles.flex('data-mapping'),
        }}
        disableGutters
      >
        {renderSectionHeader(
          'data-mapping',
          'Data Mapping',
          'Define the data mapping configuration and rules',
          <CodeIcon color="primary" />
        )}
        <AccordionDetails
          sx={{ p: 0, height: '100%', '&.MuiAccordionDetails-root': { padding: 0 } }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Define the mapping instructions
              </Typography>
              {renderEditButton('data_mapping')}
              {renderGenerateCodeButton()}
            </Box>
            {renderContent('data_mapping', localDataMappingContent)}
          </Box>
        </AccordionDetails>
      </Accordion>
    ),
    [
      expandedSections,
      handleChange,
      accordionStyles,
      renderSectionHeader,
      renderEditButton,
      renderGenerateCodeButton,
      renderContent,
      localDataMappingContent,
    ]
  )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Data Models Section */}
      {DataModelsAccordion}

      {/* Data Mapping Section */}
      {DataMappingAccordion}

      {/* Drawers */}
      <DataModelSchemaStructureDrawer
        open={dialogs.inputStructure}
        onClose={closeInputDataModelStructureDialog}
        dataModel={dataModels.input.dataModel}
        title="Input Data Model Schema Structure"
      />
      <DataModelSchemaStructureDrawer
        open={dialogs.outputStructure}
        onClose={closeOutputDataModelStructureDialog}
        dataModel={dataModels.output.dataModel}
        title="Output Data Model Schema Structure"
      />

      {/* Specification Editor Dialog */}
      {dialogConfig && (
        <SpecificationEditorDialog
          open={dialogOpen}
          onClose={closeEditDialog}
          onSave={handleDialogSave}
          title={dialogConfig.title}
          content={dialogConfig.content}
          contentType={dialogConfig.contentType}
          disabled={disabled}
          translationMap={translationMap}
          field={dialogConfig.field}
          currentSpecification={currentSpecification}
        />
      )}
    </Box>
  )
}

export default SpecificationSections
